export function rewardState(cart, config, conversion = null) {
  if (!cart || cart.currency !== config.currency || !Array.isArray(cart.items)) return null;
  const money = (value) => Number.isSafeInteger(value) && value >= 0;
  if (!money(cart.total_price)) return null;
  const rate = config.currency === config.baseCurrency ? 1 : Number(conversion);
  const convert = amount => rate > 0 && Number.isFinite(rate)
    ? Math.ceil(amount * rate - 0.000001) : null;
  const tiers = [];
  if (config.firstDiscountThreshold > 0 && config.firstDiscountThreshold < config.discountThreshold) tiers.push({
    name: 'firstDiscount', percent: 10, threshold: convert(config.firstDiscountThreshold),
    title: config.firstDiscountTitle || 'Extra 10% off', label: config.firstDiscountLabel, savings: 0,
  });
  tiers.push({ name: 'discount', percent: 20, threshold: convert(config.discountThreshold),
    title: config.discountTitle, label: config.discountLabel, savings: 0 });
  const rewardTier = discount => discount && ['automatic', '', null, undefined].includes(discount.type)
    && discount.value_type === 'percentage' ? tiers.find(tier => Number(discount.value) === tier.percent
      && (discount.title === tier.title || (tier.percent === 20 && discount.title === 'meriphora Cart Reward 20%'))) : null;
  let before = 0;
  let after = 0;
  for (const item of cart.items) {
    if (!money(item.original_line_price) || !money(item.final_line_price)) return null;
    const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
    const itemSavings = allocations.reduce((sum, allocation) => {
      const tier = rewardTier(allocation.discount_application);
      if (!tier || !money(allocation.amount)) return sum;
      tier.savings += allocation.amount;
      return sum + allocation.amount;
    }, 0);
    if (!item.gift_card && !item.selling_plan_allocation) {
      before += item.original_line_price;
      // Only add back this reward; earlier product discounts still reduce qualification.
      after += Math.min(item.original_line_price, item.final_line_price + itemSavings);
    }
  }
  const qualifying = Math.min(before, after);
  const applications = (cart.cart_level_discount_applications || []).map((entry) => entry.discount_application
    ? { ...entry.discount_application, total_allocated_amount: entry.discount_application.total_allocated_amount ?? entry.amount }
    : entry);
  for (const tier of tiers) {
    const cartSavings = applications.reduce((sum, discount) => rewardTier(discount) === tier
      && money(discount.total_allocated_amount) ? sum + discount.total_allocated_amount : sum, 0);
    tier.savings = Math.max(tier.savings, cartSavings);
    tier.applied = tier.savings > 0;
    tier.remaining = tier.threshold ? Math.max(tier.threshold - qualifying, 0) : null;
  }
  const finalTier = tiers.at(-1);
  const savings = tiers.reduce((sum, tier) => sum + tier.savings, 0);
  const reward = savings > 0;
  const shipping = config.shippingThreshold > 0 && cart.requires_shipping !== false;
  const shippingThreshold = config.shippingBaseCurrency && config.shippingBaseCurrency !== config.currency
    ? convert(config.shippingThreshold) : config.shippingThreshold;
  return {
    empty: cart.item_count === 0,
    shipping,
    shippingThreshold,
    shippingRemaining: shipping ? shippingThreshold ? Math.max(shippingThreshold - cart.total_price, 0) : null : 0,
    shippingProgress: shipping && shippingThreshold ? Math.min(cart.total_price / shippingThreshold, 1) : 0,
    discount: config.discountMode !== 'off',
    tiers,
    firstDiscountApplied: Boolean(tiers.length > 1 && reward),
    discountRemaining: finalTier.remaining,
    discountProgress: finalTier.applied ? 1 : finalTier.threshold ? Math.min(qualifying / finalTier.threshold, 1) : 0,
    discountThreshold: finalTier.threshold,
    progressAmount: Math.min(cart.total_price, qualifying),
    qualifying,
    discountApplied: finalTier.applied,
    savings,
    approximate: config.currency !== config.baseCurrency,
    preview: config.discountMode === 'preview',
  };
}

export function rewardPresentation(state, config) {
  const shippingDone = state.shipping && state.shippingRemaining === 0;
  if (state.tiers?.length > 1) {
    const points = [];
    if (state.shipping) points.push({ name: 'shipping', done: shippingDone, threshold: state.shippingThreshold });
    if (state.discount) points.push(...state.tiers.map(tier => ({ ...tier,
      done: tier.applied || state.tiers.some(higher => higher.percent > tier.percent && higher.applied),
    })));
    let progress = 0;
    let previousThreshold = 0;
    for (let index = 0; index < points.length; index++) {
      const point = points[index];
      const end = index === points.length - 1 ? 1 : (index + 0.5) / points.length;
      if (point.done) {
        progress = end;
        previousThreshold = point.threshold || previousThreshold;
        continue;
      }
      const amount = point.name === 'shipping' ? state.shippingProgress : point.threshold
        ? Math.max(0, Math.min((state.qualifying - previousThreshold) / (point.threshold - previousThreshold), 1)) : 0;
      progress = Math.min(end - 0.01, progress + (end - progress) * amount);
      break;
    }
    if (state.shipping && !shippingDone) return { progress,
      key: state.shippingRemaining === null ? 'shippingBase' : state.approximate ? 'shippingRemainingApprox' : 'shippingRemaining',
      amount: state.shippingRemaining };
    if (state.discountApplied) return { progress, key: shippingDone ? 'bothUnlocked' : 'discountUnlocked' };
    if (state.discount) {
      const next = state.tiers.find(tier => !tier.applied) || state.tiers.at(-1);
      const extra = { discountLabel: next.label };
      if (state.preview) return { progress, key: 'preview', ...extra };
      if (next.remaining === null) return { progress, key: shippingDone ? 'shippingUnlocked' : 'checkout', ...extra };
      if (next.remaining === 0) return { progress, key: 'eligible', ...extra };
      if (state.firstDiscountApplied) return { progress, key: state.approximate ? 'firstThenDiscountApprox' : 'firstThenDiscount', amount: next.remaining, ...extra };
      return { progress, key: shippingDone
        ? state.approximate ? 'shippingThenDiscountApprox' : 'shippingThenDiscount'
        : state.approximate ? 'discountRemainingApprox' : 'discountRemaining', amount: next.remaining, ...extra };
    }
    return { progress, key: 'shippingUnlocked' };
  }
  let progress = state.shipping ? state.shippingProgress : state.discountProgress;
  if (state.shipping && state.discount) {
    // The shipping icon is centred in the first of two equal columns (25%).
    const shippingStop = 0.25;
    progress = state.shippingProgress * shippingStop;
    if (shippingDone) {
      progress = shippingStop;
      if (state.discountApplied) {
        progress = 1;
      } else if (state.discountThreshold > state.shippingThreshold) {
        const second = Math.max(0, Math.min(
          (state.progressAmount - state.shippingThreshold) / (state.discountThreshold - state.shippingThreshold), 1,
        ));
        progress = Math.min(0.99, shippingStop + (1 - shippingStop) * second);
      }
    }
  }
  if (state.shipping && !shippingDone) return { progress, key: 'shippingRemaining', amount: state.shippingRemaining };
  if (state.discountApplied) return { progress, key: shippingDone ? 'bothUnlocked' : 'discountUnlocked' };
  if (state.discount) {
    if (state.preview) return { progress, key: 'preview' };
    if (state.discountRemaining === null) return { progress, key: shippingDone ? 'shippingUnlocked' : 'checkout' };
    if (state.discountRemaining === 0) return { progress, key: 'eligible' };
    return { progress, key: shippingDone
      ? state.approximate ? 'shippingThenDiscountApprox' : 'shippingThenDiscount'
      : state.approximate ? 'discountRemainingApprox' : 'discountRemaining', amount: state.discountRemaining };
  }
  return { progress, key: 'shippingUnlocked' };
}

if (typeof window !== 'undefined' && !customElements.get('meriphora-cart-rewards')) {
  const instances = new Set();
  let requestId = 0;
  let timer;
  let controller;
  let latestCart;

  function isCart(cart) {
    return cart && Array.isArray(cart.items) && typeof cart.currency === 'string'
      && Number.isSafeInteger(cart.total_price);
  }

  function update(cart) {
    latestCart = cart;
    for (const instance of instances) instance.update(cart);
  }

  async function refresh(event) {
    if (event?.detail?.data?.didError || !instances.size) return;
    const sequence = ++requestId;
    controller?.abort();
    clearTimeout(timer);
    const resource = event?.detail?.resource;
    if (isCart(resource)) {
      update(resource);
      // Native cart hydration runs after this event and can replace the meter's markup.
      queueMicrotask(() => { if (sequence === requestId) update(resource); });
      return;
    }
    latestCart = undefined;
    timer = setTimeout(async () => {
      controller = new AbortController();
      try {
        const first = instances.values().next().value;
        if (!first) return;
        const response = await fetch(first.dataset.cartUrl, {
          headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal,
        });
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        if (sequence === requestId && isCart(cart)) update(cart);
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === requestId) {
          for (const instance of instances) instance.hidden = true;
        }
      }
    }, 30);
  }

  class MeriphoraCartRewards extends HTMLElement {
    connectedCallback() {
      if (instances.has(this)) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-rewards-config]').textContent);
        const cart = JSON.parse(this.querySelector('[data-rewards-cart]').textContent);
        this.update(latestCart?.currency === this.config.currency ? latestCart : cart);
        instances.add(this);
        if (instances.size === 1) {
          document.addEventListener('cart:update', refresh, true);
          document.addEventListener('discount:update', refresh, true);
          window.addEventListener('pageshow', refresh);
        }
      } catch {
        this.hidden = true;
      }
    }

    disconnectedCallback() {
      instances.delete(this);
      if (!instances.size) {
        requestId++;
        clearTimeout(timer);
        controller?.abort();
        document.removeEventListener('cart:update', refresh, true);
        document.removeEventListener('discount:update', refresh, true);
        window.removeEventListener('pageshow', refresh);
      }
    }

    update(cart) {
      const currency = window.Shopify?.currency;
      const rate = currency?.active === this.config.currency ? currency.rate : null;
      const state = rewardState(cart, this.config, rate);
      if (!state) {
        this.hidden = true;
        return;
      }
      this.hidden = (!state.shipping && !state.discount) || (state.empty && this.config.hideEmpty);
      const format = (amount) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: cart.currency, currencyDisplay: 'code',
        minimumFractionDigits: 0, maximumFractionDigits: 2,
      }).format(amount / 100);
      const text = (key, amount) => this.config.messages[key].replace('__AMOUNT__', amount ?? '');
      this.setAttribute('data-single-reward', String(!state.shipping || !state.discount));
      this.style.setProperty('--reward-count', String(Number(state.shipping) + (state.discount ? state.tiers.length : 0)));
      for (const [name, visible, unlocked] of [
        ['shipping', state.shipping, state.shippingRemaining === 0],
        ['firstDiscount', state.discount && state.tiers.length > 1, state.firstDiscountApplied],
        ['discount', state.discount, state.discountApplied],
      ]) {
        const milestone = this.querySelector(`[data-reward="${name}"]`);
        if (!milestone) continue;
        milestone.hidden = !visible;
        milestone.toggleAttribute('data-unlocked', unlocked);
      }
      const presentation = rewardPresentation(state, this.config);
      const meter = this.querySelector('[role="progressbar"]');
      meter.setAttribute('aria-valuenow', String(Math.round(presentation.progress * 100)));
      this.querySelector('[data-reward-fill]').style.width = `${presentation.progress * 100}%`;
      const status = this.querySelector('[data-rewards-status]');
      const message = text(presentation.key, presentation.amount === undefined ? '' : format(presentation.amount));
      const discountLabel = presentation.discountLabel || this.config.discountLabel;
      const plainMessage = message.replaceAll('__SHIPPING__', this.config.shippingLabel)
        .replaceAll('__FIRST__', this.config.firstDiscountLabel || '').replaceAll('__DISCOUNT__', discountLabel);
      if (this.lastMessage !== message || status.textContent.trim() !== plainMessage) {
        const parts = message.split(/(__SHIPPING__|__FIRST__|__DISCOUNT__)/);
        status.replaceChildren(...parts.map(part => {
          if (!['__SHIPPING__', '__FIRST__', '__DISCOUNT__'].includes(part)) return document.createTextNode(part);
          const strong = document.createElement('strong');
          strong.textContent = part === '__SHIPPING__' ? this.config.shippingLabel
            : part === '__FIRST__' ? this.config.firstDiscountLabel : discountLabel;
          return strong;
        }));
        this.lastMessage = message;
      }
    }
  }
  customElements.define('meriphora-cart-rewards', MeriphoraCartRewards);
}
